import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useAuth } from "./useAuth";
import React, { useEffect } from "react";

// Subject interfaces
export interface Subject {
  id: number;
  name: string;
  subjectCode: string;
  description: string;
  classId: number;
  className: string;
  subjectCategory: string;
  active: boolean;
  schoolId: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface SubjectFormData extends Omit<Subject, "id" | "createdAt" | "updatedAt"> {
  id?: number;
  sessionId?: number;
}

const API_URL = `${process.env.REACT_APP_API_URL}/subjects`;

// API functions
const getAllSubjects = async (schoolId?: number): Promise<Subject[]> => {
  const endpoint = schoolId ? `${API_URL}?schoolId=${schoolId}` : API_URL;
  const response = await axios.get(endpoint);
  return response.data;
};

const getSubjectById = async (id: number): Promise<Subject> => {
  const response = await axios.get(`${API_URL}/${id}`);
  return response.data;
};

const getVocationalSubjects = async (classId: number): Promise<Set<string>> => {
  const response = await axios.get(`${API_URL}/vocational/${classId}`);
  return new Set(response.data.map((name: string) => name.toLowerCase().trim()));
};

const getVocationalSubjectsByClassName = async (className: string): Promise<Set<string>> => {
  const safeName = encodeURIComponent(String(className));
  const response = await axios.get(`${API_URL}/vocational/className/${safeName}`);
  return new Set(response.data.map((name: string) => name.toLowerCase().trim()));
};

const getSubjectsByClassId = async (classId: number): Promise<Subject[]> => {
  const response = await axios.get(`${API_URL}/class/id/${classId}`);
  return response.data;
};

const getSubjectsByClass = async (className: string): Promise<Subject[]> => {
  const response = await axios.get(`${API_URL}/class/name/${className}`);
  return response.data;
};

const getSubjectByCode = async (subjectCode: string): Promise<Subject> => {
  const response = await axios.get(`${API_URL}/code/${subjectCode}`);
  return response.data;
};

const createSubject = async (data: SubjectFormData): Promise<Subject> => {
  const response = await axios.post(API_URL, data);
  return response.data;
};

const createBulkSubject = async (subjects: SubjectFormData[]): Promise<Subject[]> => {
  const response = await axios.post(`${API_URL}/bulk`, subjects);
  return response.data;
};

const updateSubject = async (id: number, data: SubjectFormData): Promise<Subject> => {
  // Remove UI-only fields before sending to API
  const { activeDisplay, ...cleanData } = data as SubjectFormData & { activeDisplay?: string };
  const response = await axios.put(`${API_URL}/${id}`, cleanData);
  return response.data;
};

const deleteSubject = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/${id}`);
};

// Main hook for all subjects
export function useSubjects(tenantLoading: boolean = false, schoolId?: number) {
  const queryClient = useQueryClient();

  // Clear all school-specific caches when schoolId changes
  useEffect(() => {
    if (schoolId) {
      // Remove all other school caches to prevent cross-school data
      queryClient.removeQueries({
        queryKey: ["subjects"],
        predicate: (query) => {
          // For subjects, we need to check if the data contains subjects from other schools
          const queryData = query.state.data as Subject[] | undefined;
          if (queryData && Array.isArray(queryData)) {
            return queryData.some(subject => subject.schoolId !== schoolId);
          }
          return false;
        }
      });
    }
  }, [schoolId, queryClient]);

  const subjectsQuery = useQuery({
    queryKey: ["subjects", schoolId],
    queryFn: () => getAllSubjects(schoolId),
    enabled: !tenantLoading,
    staleTime: 0, // Always fetch fresh data when schoolId changes
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  const createSubjectMutation = useMutation({
    mutationFn: createSubject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subjects"] });
      queryClient.invalidateQueries({ queryKey: ["subjects-class-id"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["subjects", schoolId] });
      }
    }
  });

  const createBulkSubjectMutation = useMutation({
    mutationFn: createBulkSubject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subjects"] });
      queryClient.invalidateQueries({ queryKey: ["subjects-class-id"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["subjects", schoolId] });
      }
    }
  });

  const updateSubjectMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: SubjectFormData }) => updateSubject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subjects"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["subjects", schoolId] });
      }
    },
  });

  const deleteSubjectMutation = useMutation({
    mutationFn: deleteSubject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subjects"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["subjects", schoolId] });
      }
    },
  });

  return {
    subjectsQuery,
    createSubjectMutation,
    createBulkSubjectMutation,
    updateSubjectMutation,
    deleteSubjectMutation,
  };
}

// Custom hooks for specific queries
export function useSubjectById(id: number, tenantLoading: boolean = false) {
  return useQuery({
    queryKey: ["subject", id],
    queryFn: () => getSubjectById(id),
    enabled: !!id && !tenantLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useSubjectsByClassId(classId: number, tenantLoading: boolean = false) {
  const query = useQuery({
    queryKey: ["subjects-class-id", classId],
    queryFn: () => {
      return getSubjectsByClassId(classId);
    },
    enabled: Number(classId) > 0 && !tenantLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  return query;
}

export function useSubjectsByClass(className: string, tenantLoading: boolean = false) {
  const query = useQuery({
    queryKey: ["subjects-class", className],
    queryFn: () => {
      return getSubjectsByClass(className);
    },
    enabled: !!className && !tenantLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
  return query;
}

export function useSubjectByCode(subjectCode: string, tenantLoading: boolean = false) {
  return useQuery<Subject, Error>({
    queryKey: ["subject-code", subjectCode],
    queryFn: () => getSubjectByCode(subjectCode),
    enabled: !!subjectCode && !tenantLoading,
  });
}

// Hook to get vocational subjects for a class
export function useVocationalSubjects(classId: number | null | undefined, tenantLoading: boolean = false) {
  return useQuery<Set<string>, Error>({
    queryKey: ["vocational-subjects", classId],
    queryFn: () => getVocationalSubjects(classId!),
    enabled: Number(classId) > 0 && !tenantLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useVocationalSubjectsByClassName(className: string, tenantLoading: boolean = false) {
  return useQuery<Set<string>, Error>({
    queryKey: ["vocational-subjects", "className", className],
    queryFn: () => getVocationalSubjectsByClassName(className),
    enabled: !!className && !tenantLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Export the function for direct use if needed
export { getVocationalSubjects };
export { useSubjectMarksMappingByClassName } from "./useSubjectMarkMapping";
